import { FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Content, Forms, Grid, Modals, toast } from "components";
import { api, signup } from "services";

import { useDebounce } from "hooks/debounce";
import { countries } from "utils";
import { responsibleSchema } from "../../../schema";
import * as S from "../../../styles";
import { relationship_degree } from "../../../utils";

const initialValues = {
  cpf: "",
  name: "",
  email: "",
  address: {
    city: "",
    state: "",
    number: "",
    street: "",
    zipcode: "",
    complement: "",
    neighborhood: "",
  },
  foreigner: {
    enabled: false,
    country: "",
  },
  pep: {
    enabled: false,
    relation: {
      enabled: false,
      degree: "",
    },
  },
};

export type PartnerValues = typeof initialValues;

const ResponsiblePersonRegister = (props: any) => {
  const [status, setStatus] = useState<
    "fetching-document" | "fetched-document" | "fetching-address" | ""
  >("");

  const isEditing = useMemo(() => !!props.initialValues, [props.initialValues]);

  const form = useFormik<PartnerValues>({
    initialValues: initialValues,
    validationSchema: responsibleSchema,
    onSubmit: (values) => {
      props.onSubmit(values);
    },
  });

  const [document_, setDocument] = useState("");
  const [zipcode_, setZipcode] = useState("");

  const zipcode = useDebounce(zipcode_);
  const document = useDebounce(document_);

  const fetchAddress = useCallback(async () => {
    try {
      setStatus("fetching-address");

      const address = await api.addresses.getByZipcode(zipcode);

      for (const [key, value] of Object.entries(address)) {
        form.setFieldValue(`address.${key}`, value);
      }
    } catch (error) {
      toast("Por favor insira um CEP válido.", {
        type: "warning",
      });
    } finally {
      setStatus("");
    }
  }, [zipcode]);

  const fetchDocumentDetails = useCallback(async () => {
    try {
      setStatus("fetching-document");

      const { name } = await signup.findPersonByDocument(document);

      form.setFieldValue("name", name);
    } finally {
      setStatus("");
    }
  }, [document]);

  useEffect(() => {
    if (zipcode && !form.errors.address?.zipcode) fetchAddress();
  }, [zipcode]); // eslint-disable-line

  useEffect(() => {
    if (document && !form.errors.cpf) fetchDocumentDetails();
  }, [document]);

  useEffect(() => {
    if (props.initialValues) {
      form.setValues(props.initialValues);
    }
  }, []);

  return (
    <Modals.Content
      icon="person"
      title={isEditing ? form.values.name : "Cadastrar responsável"}
      onCancel={{
        text: "Cancelar",
        onClick: props.onDismiss,
      }}
      onSubmit={{
        text: isEditing ? "Salvar alterações" : "Cadastrar responsável",
        onClick: () => form.handleSubmit(),
      }}
    >
      <FormikProvider value={form}>
        <Content.Message kind="warning">
          Por determinação da SUSEP o corretor <b>não</b> pode ser o Estipulante
          do contrato.
        </Content.Message>

        <Grid gap={[".5rem", "1rem"]} space={["1rem 0 0", "2rem 0 0"]}>
          <Forms.InputGroup
            name="cpf"
            label="CPF"
            placeholder="Digite o CPF"
            mask="cpf"
            loading={status === "fetching-document"}
            onKeyUp={(event) => {
              setDocument(event.target.value);
            }}
          />
        </Grid>

        <Grid
          columns={["auto", "repeat(2, 1fr)"]}
          space="1rem 0 0"
          gap={[".5rem", "1rem"]}
        >
          <Forms.InputGroup
            name="name"
            label="Nome"
            placeholder="Digite o nome"
          />

          <Forms.InputGroup
            name="email"
            label="E-mail pessoal"
            placeholder="Digite o e-mail pessoal"
          />
        </Grid>

        <Grid space={["1rem 0 0", "2rem 0 0"]} gap={[".5rem", "1rem"]}>
          <S.ChoiceWrapper>
            <Forms.Toggle label="Estrangeiro" name="foreigner.enabled" />

            <Forms.Toggle
              label="Pessoa exposta politicamente"
              name="pep.enabled"
            />

            <Forms.Toggle
              label="Grau de relacionamento com a PEP"
              name="pep.relation.enabled"
            />
          </S.ChoiceWrapper>
        </Grid>

        {(form.values.foreigner.enabled ||
          form.values.pep.relation.enabled) && (
          <>
            <Grid
              columns={["auto", "repeat(2, 1fr)"]}
              space={["1rem 0 0", "2rem 0 0"]}
              gap={[".5rem", "1rem"]}
            >
              {form.values.foreigner.enabled && (
                <Forms.SelectGroup
                  name="foreigner.country"
                  label="País de residência"
                  options={[
                    {
                      label: "Selecione o país de residência",
                      value: "",
                    },
                    ...countries.map(({ name }) => ({
                      label: name,
                      value: name,
                    })),
                  ]}
                />
              )}

              {form.values.pep.relation.enabled && (
                <Forms.SelectGroup
                  name="pep.relation.degree"
                  label="Grau de relacionamento"
                  options={relationship_degree}
                />
              )}
            </Grid>
          </>
        )}

        <Grid
          columns={["auto", "repeat(2, 1fr)"]}
          space={["1rem 0 0", "2rem 0 0"]}
          gap={[".5rem", "1rem"]}
        >
          <Forms.InputGroup
            name="address.zipcode"
            label="CEP"
            placeholder="Digite o CEP"
            mask="zipcode"
            loading={status === "fetching-address"}
            onKeyUp={(event) => {
              setZipcode(event.target.value);
            }}
          />
        </Grid>

        {!!form.values.address.city && (
          <>
            <Grid
              columns={["auto", "auto .2fr .5fr"]}
              gap="1rem"
              space={["1rem 0", "2rem 0"]}
            >
              <Forms.InputGroup
                name="address.street"
                label="Rua"
                placeholder="Confirme a rua"
              />

              <Forms.InputGroup
                name="address.number"
                label="Número"
                placeholder="Ex: 130"
              />

              <Forms.InputGroup
                name="address.complement"
                label="Complemento"
                placeholder="Ex.: Bloco 10; Apto 172"
              />
            </Grid>

            <Grid columns={["repeat(2, 1fr)", "1fr 1fr .5fr"]} gap="1rem">
              <Forms.InputGroup
                name="address.neighborhood"
                label="Bairro"
                placeholder="Confirme o bairro"
              />

              <Forms.InputGroup
                name="address.city"
                label="Cidade"
                placeholder="Confirme a cidade"
              />

              <Forms.InputGroup
                name="address.state"
                label="Estado"
                placeholder="Confirme o estado"
              />
            </Grid>
          </>
        )}
      </FormikProvider>
    </Modals.Content>
  );
};

// eslint-disable-next-line
export default (props?: any) =>
  Modals.Promised.createModal(ResponsiblePersonRegister, props);
